import React, { useMemo } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { Language } from '@rbilabs/intl';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import PickupTimeSelector from 'components/pickup-time-selector';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { OrderStatus, ServiceMode, useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { formatDistanceToNow } from 'utils/language/format-date';
import { routes } from 'utils/routing';
import { isCateringDelivery, isCateringPickup, isDelivery } from 'utils/service-mode';

import { RestaurantDetailsModal } from '../restaurant-details-modal';
import SectionHeading from '../section-heading';
import { ServiceModeDetails } from '../service-mode-details';

import ChangeLink from './change-link';
import {
  getLocalizedDateTime,
  getLocalizedDeliveryAddress,
  getLocalizedStoreAddress,
} from './common';
import DeliveryDetails from './delivery-details';
import OrderPhoneInput from './order-phone-input';
import { DetailsBox, InstructionsTextarea, Text } from './styled';
import { ICartDetailsProps } from './types';
import { formatPhoneNumber, localizedDateTimeFormatter } from './utils';

const HeadingContainer = styled.div`
  display: flex;
`;

const PickupTimeHeadingContainer = styled(HeadingContainer)`
  ${Styles.mobile`
    margin: 0 1rem;
  `}
`;

const HeadingContainerWrapper = styled(HeadingContainer)`
  margin-top: 3rem;
`;

const Spacer = styled.div`
  margin-top: 0.5rem;
  margin-bottom: 0.4rem;
`;

const PickupDetailsBox = styled(DetailsBox)`
  padding: 0;
  background-color: ${Styles.color.cardBackground};
`;

const PickupDetailsAddress = styled.div`
  padding: 1.25rem 1.5rem;
`;

const CateringPickupDetailsBox = styled(DetailsBox)`
  & > * {
    margin-bottom: 1rem;
  }

  & :last-child {
    margin-bottom: 0;
  }
`;

const InstructionsTextAreaCatering = styled(InstructionsTextarea)`
  height: 4rem;
  border-color: ${Styles.color.grey.five};
  padding: 0.5rem 0.8rem;
`;

const DetailBlock = styled.div`
  display: flex;
`;

const DetailBlockContent = styled.div`
  margin-left: 1rem;
  ${Styles.mobile`
    margin-left: 0.5rem;
  `}
`;

const DetailBlockHeader = styled.div`
  font-weight: ${Styles.fontWeight.heavy};
`;

const PickupTimeContainer = styled.div`
  margin: 1rem 0 -1rem;
  ${Styles.mobile`
      margin: 1rem -1rem -1rem;
  `}
`;

const method = (serviceMode: ServiceMode) => {
  return {
    [ServiceMode.EAT_IN]: 'Dine In',
    [ServiceMode.TAKEOUT]: 'Pick Up',
    [ServiceMode.DRIVE_THRU]: 'Drive Thru',
    [ServiceMode.CURBSIDE]: 'Curbside',
    [ServiceMode.DELIVERY]: 'Delivery',
    [ServiceMode.CATERING_DELIVERY]: 'Catering Delivery',
    [ServiceMode.CATERING_PICKUP]: 'Catering Pickup',
    [ServiceMode.TABLE_SERVICE]: 'Table Service',
  }[serviceMode];
};

const CartDetails: React.FC<ICartDetailsProps> = ({
  className = '',
  readOnly = false,
  onTimeSelectorChange,
  onDeliveryStatusChange,
  timeSelectorValue,
  serverOrder,
  serviceMode,
  enableOrderTimedFire,
  enableDeliveryOrderTimedFire,
  enableDriveThruOrderTimedFire,
  isPickupTimeAvailable,
  user,
  setAreDeliveryDetailsValid,
}) => {
  let details;
  let sectionHeading = '';
  const { setStoreLocatorCallbackUrl, location, navigate } = useLocationContext();

  if (isDelivery(serviceMode)) {
    sectionHeading = 'deliveryDetails';
    details = (
      <DeliveryDetails
        serverOrder={serverOrder}
        setAreDeliveryDetailsValid={setAreDeliveryDetailsValid}
      />
    );
  } else if (isCateringPickup(serviceMode)) {
    sectionHeading = 'restaurantDetails';
    details = (
      <CateringPickupDetails user={user} setAreDeliveryDetailsValid={setAreDeliveryDetailsValid} />
    );
  } else if (isCateringDelivery(serviceMode)) {
    sectionHeading = 'deliveryDetails';
    details = <CateringDeliveryDetails serverOrder={serverOrder} user={user} />;
  } else {
    sectionHeading = 'restaurantDetails';
    details = <PickupDetails serviceMode={serviceMode} readOnly={readOnly} />;
  }

  const isCateringConfirmation = serverOrder?.status === OrderStatus.CATERING_PLACED;
  const { formatMessage } = useIntl();

  const enablePickupTimeDrivethru = useFlag(LaunchDarklyFlag.ENABLE_PICKUPTIME_DRIVETHRU);

  const isTakeout = serviceMode === ServiceMode.TAKEOUT;
  const isEatIn = serviceMode === ServiceMode.EAT_IN;
  const isDriveThru = serviceMode === ServiceMode.DRIVE_THRU;

  const shouldRenderTimedFire =
    ((isTakeout || isEatIn) && enableOrderTimedFire) ||
    (isDelivery(serviceMode) && enableDeliveryOrderTimedFire) ||
    (isDriveThru &&
      enableOrderTimedFire &&
      enableDriveThruOrderTimedFire &&
      enablePickupTimeDrivethru);

  return (
    <div className={className}>
      <HeadingContainer>
        <SectionHeading id="order-details-section-heading">
          {formatMessage({
            id: sectionHeading,
          })}
        </SectionHeading>
        {!isCateringConfirmation && (
          <ChangeLink
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();

              setStoreLocatorCallbackUrl(location.pathname);
              navigate(routes.serviceMode);
            }}
            to={routes.serviceMode}
            aria-label={formatMessage({
              id: isDelivery(serviceMode) ? 'changeAddressAriaLabel' : 'changeStoreAriaLabel',
            })}
          >
            {formatMessage({ id: isDelivery(serviceMode) ? 'changeAddress' : 'changeStores' })}
          </ChangeLink>
        )}
      </HeadingContainer>
      {details}
      {shouldRenderTimedFire && (
        <PickupTimeContainer>
          <PickupTimeHeadingContainer>
            <SectionHeading id="order-details-section-heading">
              {isDelivery(serviceMode)
                ? formatMessage({ id: 'deliveryTime' })
                : formatMessage({ id: 'pickUpTime' })}
            </SectionHeading>
          </PickupTimeHeadingContainer>
          <PickupTimeSelector
            onChange={value => onTimeSelectorChange && onTimeSelectorChange(value)}
            onDeliveryStatusChange={onDeliveryStatusChange}
            isPickupTimeAvailable={isPickupTimeAvailable}
            showAsTime
            value={timeSelectorValue || 0}
          />
        </PickupTimeContainer>
      )}
    </div>
  );
};

const CateringPickupDetails: React.FC<Omit<ICartDetailsProps, 'serverOrder' | 'serviceMode'>> = ({
  user,
  setAreDeliveryDetailsValid,
}) => {
  const { formatMessage } = useIntl();
  const { locale } = useLocale();
  const { cateringPickupDateTime = '' } = useOrderContext() || {};
  const { store } = useStoreContext();
  const localizedStoreAddress = getLocalizedStoreAddress(store, formatMessage);
  const formattedPickupDateTime = localizedDateTimeFormatter(cateringPickupDateTime, locale);
  const localizedPickupDateTime = getLocalizedDateTime(formattedPickupDateTime, formatMessage);

  return (
    <>
      <CateringPickupDetailsBox aria-labelledby="order-details-section-heading">
        <DetailBlock>
          <Icon icon="account" color="icon-active" width="24px" height="24px" aria-hidden />
          <DetailBlockContent>
            <DetailBlockHeader>
              {formatMessage({
                id: 'orderPlacedBy',
              })}
              :
            </DetailBlockHeader>
            {user && <div>{user.details.name}</div>}
            {<OrderPhoneInput onValidStateChange={setAreDeliveryDetailsValid} />}
          </DetailBlockContent>
        </DetailBlock>

        <DetailBlock>
          <Icon icon="location" color="icon-active" width="24px" height="24px" aria-hidden />
          <DetailBlockContent>
            <DetailBlockHeader>
              {formatMessage({
                id: 'pickUpAt',
              })}
              :
            </DetailBlockHeader>
            {localizedStoreAddress}
          </DetailBlockContent>
        </DetailBlock>

        <DetailBlock>
          <Icon icon="time" color="icon-active" width="24px" height="24px" aria-hidden />
          <DetailBlockContent>
            <DetailBlockHeader>
              {formatMessage({
                id: 'pickUpTime',
              })}
              :
            </DetailBlockHeader>
            {localizedPickupDateTime}
          </DetailBlockContent>
        </DetailBlock>
      </CateringPickupDetailsBox>
    </>
  );
};

const PickupDetails: React.FC<Omit<ICartDetailsProps, 'serverOrder'>> = ({ serviceMode }) => {
  const { store } = useStoreContext();
  const { formatMessage } = useIntl();

  const enableServiceModeCartSelection = useFlag(
    LaunchDarklyFlag.ENABLE_SERVICE_MODE_CART_SELECTION
  );
  const enableCheckoutModalFzInformation = useFlag(
    LaunchDarklyFlag.ENABLE_CHECKOUT_MODAL_FZ_INFORMATION
  );

  const localizedStoreAddress = getLocalizedStoreAddress(store, formatMessage);

  return (
    <PickupDetailsBox
      tabIndex={0}
      aria-labelledby="order-details-section-heading pickup-details-address"
    >
      <PickupDetailsAddress id="pickup-details-address">
        <Text>{localizedStoreAddress}</Text>

        {!enableServiceModeCartSelection && (
          <div>{formatMessage({ id: 'method' }, { serviceMode: method(serviceMode) })}</div>
        )}

        {enableCheckoutModalFzInformation && <RestaurantDetailsModal />}
      </PickupDetailsAddress>
      {enableServiceModeCartSelection && <ServiceModeDetails />}
    </PickupDetailsBox>
  );
};

const CateringDeliveryDetails: React.FC<Omit<ICartDetailsProps, 'serviceMode'>> = ({
  serverOrder,
  user,
}) => {
  const { deliveryAddress, deliveryInstructions, setDeliveryInstructions } = useOrderContext();
  const maxDeliveryTime = serverOrder?.delivery?.dropoff.beforeTime || '';
  const ETA = useMemo(
    () => formatDistanceToNow(new Date(maxDeliveryTime), Language.en),
    [maxDeliveryTime]
  );
  const { locale } = useLocale();

  const handleSetInstructions = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDeliveryInstructions(e.target.value);
  };
  const enableOverrideDeliveryTime = useFlag(LaunchDarklyFlag.ENABLE_OVERRIDE_DELIVERY_TIME);
  const { formatMessage } = useIntl();
  const localizedDeliveryAddress = getLocalizedDeliveryAddress(deliveryAddress, formatMessage);

  return (
    <div>
      <DetailsBox aria-labelledby="order-details-section-heading">
        <Spacer>
          <strong>
            {formatMessage({
              id: 'orderPlacedBy',
            })}
            :
          </strong>
          {user && <div>{user.details.name}</div>}
          {user && <div>{formatPhoneNumber(user.details.phoneNumber, locale)}</div>}
        </Spacer>
        <Spacer>
          <strong>
            {formatMessage({
              id: 'deliveringTo',
            })}
            :
          </strong>
          <Text data-private>{localizedDeliveryAddress}</Text>
        </Spacer>
        <Spacer>
          <strong>
            {enableOverrideDeliveryTime
              ? ''
              : formatMessage({
                  id: 'deliveryTime',
                })}
          </strong>
          {maxDeliveryTime && (
            <Text>
              <span
                aria-label={
                  enableOverrideDeliveryTime
                    ? formatMessage({ id: 'etaFixed' })
                    : formatMessage({ id: 'etaFull' })
                }
              >
                {enableOverrideDeliveryTime
                  ? formatMessage({ id: 'etaFixed' })
                  : formatMessage({
                      id: 'eta',
                    })}
              </span>
              <strong>{enableOverrideDeliveryTime ? '' : { ETA }}</strong>
            </Text>
          )}
        </Spacer>
      </DetailsBox>

      <HeadingContainerWrapper>
        <SectionHeading>
          {formatMessage({
            id: 'deliveryInstructions',
          })}
        </SectionHeading>
      </HeadingContainerWrapper>

      <DetailsBox>
        <InstructionsTextAreaCatering
          name="deliveryInstructions"
          placeholder={formatMessage({
            id: 'addSpecificInstructions',
          })}
          aria-label={formatMessage({
            id: 'addSpecificInstructions',
          })}
          rows={1}
          value={deliveryInstructions}
          onChange={handleSetInstructions}
          maxLength={512}
          date-private
        />
      </DetailsBox>
    </div>
  );
};

export default CartDetails;
